import React, { FC, useCallback, useRef, useState } from 'react';

import classNames from 'classnames';

import { ACTION_DECREASE, ACTION_INCREASE, ACTION_SET } from '../../store/constants';
import { THEME_PRIMARY, themeColor } from '../../styles/basics/colors';
import { BOLINDA_ICON_QTY_CHANGE, MATERIAL_ICON_MINUS_ONE, MATERIAL_ICON_PLUS_ONE } from './Button/Icon';
import { Input, MATERIAL_ICON_CHECK } from './Input';
import { Button, ButtonSize, ButtonTheme } from './Button';
import { Popover } from '../components/Popover/Popover';

import './ChangeQuantityDialog.scss';
import { useTranslation } from 'react-i18next';
import { BulkActionResponse } from '../../store/api';
import { useListContentChecks } from './useListContentChecks';
import { PriceTier } from '../../../models/PriceTier';
import { UnavailableProductsModal } from './UnavailableProductsModal';

/**
 * Props for <ChangeQuantityDialog>.
 */
interface Props {
    /**
     * Callback for different actions changing the quantity.
     */
    readonly onChange: (
        action: typeof ACTION_SET | typeof ACTION_INCREASE | typeof ACTION_DECREASE,
        amount: number
    ) => Promise<BulkActionResponse>;

    /**
     * Callback when everything should be deselected.
     */
    readonly cancelProductSelection?: () => void;

    /**
     * An optional class name attached to the popover.
     */
    readonly className?: string;

    /**
     * An optional label for the button. Defaults to 'Change Quantity'.
     */
    readonly label?: string;
    readonly isNothingSelected: boolean;
}

export const ChangeQuantityDialog: FC<Props> = ({
    onChange,
    cancelProductSelection = () => {},
    className,
    label,
    isNothingSelected
}) => {
    const [showDropdown, setShowDropdown] = useState(false);
    const [quantity, setQuantity] = useState<number | undefined>(undefined);
    const { t } = useTranslation();
    const [isChangingQuantity, setChangingQuantity] = useState({
        increasing: false,
        decreasing: false,
        settingAmount: false
    });

    const buttonRef = useRef<HTMLButtonElement>(null);

    const close = useCallback(() => setShowDropdown(false), []);
    const toggle = useCallback(() => setShowDropdown(it => !it), []);

    const { changePrice, getUnavailableProductsModalProps } = useListContentChecks<{
        action: typeof ACTION_SET | typeof ACTION_INCREASE | typeof ACTION_DECREASE;
        amount: number;
        priceTier?: PriceTier;
    }>({ onAction: (_, { action, amount }) => onChange(action, amount) });

    const onDecrease = useCallback(async () => {
        setChangingQuantity(val => ({ ...val, decreasing: true }));
        try {
            return await changePrice({ action: ACTION_DECREASE, amount: 1 });
        } finally {
            setChangingQuantity(val => ({ ...val, decreasing: false }));
        }
    }, [changePrice]);

    const onIncrease = useCallback(async () => {
        setChangingQuantity(val => ({ ...val, increasing: true }));
        try {
            return await changePrice({ action: ACTION_INCREASE, amount: 1 });
        } finally {
            setChangingQuantity(val => ({ ...val, increasing: false }));
        }
    }, [changePrice]);

    const onInputChange = useCallback((value: string) => {
        const parsed = parseInt(value);
        if (!isNaN(parsed)) {
            setQuantity(parsed);
        } else {
            setQuantity(undefined);
        }
    }, []);

    const deselectAndClose = useCallback(() => {
        cancelProductSelection();
        close();
    }, [cancelProductSelection, close]);

    const onSubmit = useCallback(async () => {
        setChangingQuantity(val => ({ ...val, settingAmount: true }));
        try {
            if (quantity) {
                return await changePrice({ action: ACTION_SET, amount: quantity });
            }
        } finally {
            setChangingQuantity(val => ({ ...val, settingAmount: false }));
        }
    }, [quantity, changePrice]);

    return (
        <>
            <Button
                text={label}
                icon={BOLINDA_ICON_QTY_CHANGE}
                size={ButtonSize.SMALL}
                theme={ButtonTheme.NEUTRAL}
                onClick={toggle}
                ref={buttonRef}
                disabled={isNothingSelected}
            />
            <UnavailableProductsModal {...getUnavailableProductsModalProps()} />
            <Popover
                className={classNames('change-quantity-dialog', className)}
                target={buttonRef}
                visible={showDropdown}
                placement="bottom"
                onClose={close}
            >
                <div className="actions">
                    <Button
                        text={t('components.quantityDialog.decrease')}
                        onClick={onDecrease}
                        icon={MATERIAL_ICON_MINUS_ONE}
                        size={ButtonSize.SMALL}
                        theme={ButtonTheme.NEUTRAL}
                        disabled={
                            isChangingQuantity.increasing ||
                            isChangingQuantity.decreasing ||
                            isChangingQuantity.settingAmount
                        }
                        isLoading={isChangingQuantity.decreasing || isChangingQuantity.settingAmount}
                    />
                    <Button
                        text={t('components.quantityDialog.increase')}
                        onClick={onIncrease}
                        icon={MATERIAL_ICON_PLUS_ONE}
                        size={ButtonSize.SMALL}
                        theme={ButtonTheme.NEUTRAL}
                        disabled={
                            isChangingQuantity.increasing ||
                            isChangingQuantity.decreasing ||
                            isChangingQuantity.settingAmount
                        }
                        isLoading={isChangingQuantity.increasing || isChangingQuantity.settingAmount}
                    />
                </div>
                <div className="set-quantity">
                    <span>{t('components.quantityDialog.setQuantityTo')}</span>
                    <Input
                        width="75px"
                        value={quantity ?? ''}
                        onEnterKey={onSubmit}
                        onEscapeKey={deselectAndClose}
                        onChange={onInputChange}
                        suffixIcon={MATERIAL_ICON_CHECK}
                        placeholder={t('components.quantityDialog.quantityPlaceholder')}
                        onSuffixIconClick={onSubmit}
                        iconColor={themeColor(THEME_PRIMARY)}
                        className="input"
                        disabled={
                            isChangingQuantity.increasing ||
                            isChangingQuantity.decreasing ||
                            isChangingQuantity.settingAmount
                        }
                    />
                </div>
            </Popover>
        </>
    );
};
